import { useState } from 'react'
import { Button, Field, Heading, Input, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useCreateContact } from '../../hooks/useContactDetail'
import { useVoterFilePresent } from '../../hooks/useClientConfig'
import { useMatchContact } from '../../hooks/useMatchContact'
import { useIsMobile } from '../../hooks/useIsMobile'
import { StickyActionFooter, Card } from '../shared'
import { DistrictFields } from './DistrictFields'
import { extractApiMessage } from '../../lib/apiError'
import { isValidPhone, isValidEmail, PHONE_ERROR, EMAIL_ERROR } from '../../lib/contactMethods'
import type { CreateContactRequest, ContactMatchCandidate } from '../../types/contactDetail'

export type ContactCreateContext =
  | { kind: 'standalone' }
  | { kind: 'lobbyDay'; eventId: string }
  | { kind: 'union'; unionId?: string }

export interface ContactCreateFormProps {
  context: ContactCreateContext
  onDone: (contactId: string) => void
}

const defaultValues: CreateContactRequest = {
  firstName: '',
  middleName: '',
  lastName: '',
  nickName: '',
  dateOfBirth: '',
  streetAddress: '',
  city: '',
  state: '',
  zip: '',
  county: '',
  addressType: '',
  phone: '',
  email: '',
  locCongressional: '',
  locStateSenate: '',
  locStateHouse: '',
}

/**
 * The form's default values fill every optional field with `''` (react-hook-form has no
 * "unset" concept). Backend `CreateContactRequest` optional fields are nullable types
 * (e.g. `DateOfBirth: DateTime?`) -- ASP.NET's JSON binder throws a 400 on `""` for those,
 * so an empty-string field must become `undefined` before the payload is sent (finding #1/#6,
 * add-contact final review). Required fields (firstName/lastName) are left untouched.
 */
function normalizeForSubmit(values: CreateContactRequest): CreateContactRequest {
  const normalized = { ...values }
  for (const key of Object.keys(normalized) as (keyof CreateContactRequest)[]) {
    if (key === 'firstName' || key === 'lastName') continue
    const value = normalized[key]
    if (typeof value === 'string') {
      const trimmed = value.trim()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(normalized as any)[key] = trimmed === '' ? undefined : trimmed
    }
  }
  return normalized
}

function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/)
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') }
}

function candidateAddressLine(candidate: ContactMatchCandidate): string {
  return [candidate.streetAddress, candidate.city, candidate.state, candidate.zip].filter(Boolean).join(', ')
}

function candidateDistrictLine(candidate: ContactMatchCandidate): string | null {
  const parts = [
    candidate.locCongressional && `Congressional ${candidate.locCongressional}`,
    candidate.locStateSenate && `Senate ${candidate.locStateSenate}`,
    candidate.locStateHouse && `House ${candidate.locStateHouse}`,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

export function ContactCreateForm({ context: _context, onDone }: ContactCreateFormProps) {
  const createContact = useCreateContact()
  const matchContact = useMatchContact()
  const voterFilePresent = useVoterFilePresent()
  const isMobile = useIsMobile()

  const [suggestions, setSuggestions] = useState<ContactMatchCandidate[] | null>(null)
  const [noMatches, setNoMatches] = useState(false)
  const [adopted, setAdopted] = useState<ContactMatchCandidate | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<CreateContactRequest>({
    defaultValues,
  })

  const lastName = watch('lastName')
  const streetAddress = watch('streetAddress')
  const zip = watch('zip')
  const canMatch = !!lastName?.trim() && (!!streetAddress?.trim() || !!zip?.trim())

  const runMatch = async () => {
    const values = getValues()
    const results = await matchContact.mutateAsync({
      lastName: values.lastName,
      firstName: values.firstName || undefined,
      streetAddress: values.streetAddress || undefined,
      city: values.city || undefined,
      state: values.state || undefined,
      zip: values.zip || undefined,
    })
    if (results.length === 0) {
      setSuggestions(null)
      setNoMatches(true)
    } else {
      setNoMatches(false)
      setSuggestions(results)
    }
  }

  const pickCandidate = (candidate: ContactMatchCandidate) => {
    const { firstName, lastName: last } = splitFullName(candidate.fullName)
    setValue('firstName', firstName)
    setValue('lastName', last)
    setValue('streetAddress', candidate.streetAddress ?? '')
    setValue('city', candidate.city ?? '')
    setValue('state', candidate.state ?? '')
    setValue('zip', candidate.zip ?? '')
    setValue('locCongressional', candidate.locCongressional ?? '')
    setValue('locStateSenate', candidate.locStateSenate ?? '')
    setValue('locStateHouse', candidate.locStateHouse ?? '')
    setAdopted(candidate)
    setSuggestions(null)
    setNoMatches(false)
  }

  const dismissSuggestions = () => {
    setSuggestions(null)
    setNoMatches(false)
  }

  const useDifferentPerson = () => {
    setAdopted(null)
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      const created = await createContact.mutateAsync(normalizeForSubmit(values))
      onDone(created.id)
    } catch (error) {
      // A10: the server's 400 names the actual problem ("outside your committee's granted
      // scope"). Never replace it with a generic failure string.
      setSubmitError(extractApiMessage(error, 'Could not create the contact.'))
    }
  })

  const readOnly = !!adopted

  // Adopt mode attaches an existing (voter-file-matched) contact by id -- it does not create
  // anything, so it must not go through handleSubmit/required-validation. A matched candidate
  // with a single-word fullName splits to lastName='', which would otherwise fail "required"
  // and leave the read-only form stuck with no way to submit (finding #4, add-contact review).
  const submitAdopted = () => {
    if (adopted) onDone(adopted.contactId)
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <VStack gap={5} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <Field.Root invalid={!!errors.firstName} required>
            <Field.Label>First name</Field.Label>
            <Input readOnly={readOnly} {...register('firstName', { required: 'First name is required' })} />
            <Field.ErrorText>{errors.firstName?.message}</Field.ErrorText>
          </Field.Root>
          <Field.Root>
            <Field.Label>Middle name</Field.Label>
            <Input readOnly={readOnly} {...register('middleName')} />
          </Field.Root>
          <Field.Root invalid={!!errors.lastName} required>
            <Field.Label>Last name</Field.Label>
            <Input readOnly={readOnly} {...register('lastName', { required: 'Last name is required' })} />
            <Field.ErrorText>{errors.lastName?.message}</Field.ErrorText>
          </Field.Root>
          <Field.Root>
            <Field.Label>Nickname</Field.Label>
            <Input readOnly={readOnly} {...register('nickName')} />
          </Field.Root>
          <Field.Root>
            <Field.Label>Date of birth</Field.Label>
            <Input type="date" readOnly={readOnly} {...register('dateOfBirth')} />
          </Field.Root>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <Field.Root>
            <Field.Label>Street address</Field.Label>
            <Input readOnly={readOnly} {...register('streetAddress')} />
          </Field.Root>
          <Field.Root>
            <Field.Label>City</Field.Label>
            <Input readOnly={readOnly} {...register('city')} />
          </Field.Root>
          <Field.Root>
            <Field.Label>State</Field.Label>
            <Input readOnly={readOnly} {...register('state')} />
          </Field.Root>
          <Field.Root>
            <Field.Label>Zip</Field.Label>
            <Input readOnly={readOnly} {...register('zip')} />
          </Field.Root>
          <Field.Root>
            <Field.Label>County</Field.Label>
            <Input readOnly={readOnly} {...register('county')} />
          </Field.Root>
          <Field.Root>
            <Field.Label>Address type</Field.Label>
            <Input readOnly={readOnly} {...register('addressType')} />
          </Field.Root>
        </SimpleGrid>

        {/* Districts sit directly under the address (first-class, per Jason #1). Pick-only where
            the server can name the options for this state+chamber; free text otherwise. */}
        <VStack align="stretch" gap={3}>
          <Heading size="sm">Districts</Heading>
          <DistrictFields
            state={watch('state')}
            value={{
              locCongressional: watch('locCongressional'),
              locStateSenate: watch('locStateSenate'),
              locStateHouse: watch('locStateHouse'),
            }}
            readOnly={readOnly}
            onChange={(field, next) => setValue(field, next)}
          />
        </VStack>

        {voterFilePresent && !readOnly && (
          <VStack align="stretch" gap={3}>
            <Button
              type="button"
              variant="outline"
              colorPalette="brand"
              minH="44px"
              alignSelf="flex-start"
              disabled={!canMatch}
              loading={matchContact.isPending}
              onClick={runMatch}
            >
              Match to voter file
            </Button>

            {noMatches && (
              <Text fontSize="sm" color="ink.2">No voter-file matches found — continue creating a new contact below.</Text>
            )}

            {suggestions && suggestions.length > 0 && (
              <Card p={3}>
                <VStack align="stretch" gap={2}>
                  <Heading size="sm">Possible matches</Heading>
                  {suggestions.map((candidate) => (
                    <Button
                      key={candidate.contactId}
                      type="button"
                      variant="ghost"
                      justifyContent="flex-start"
                      minH="44px"
                      h="auto"
                      py={2}
                      onClick={() => pickCandidate(candidate)}
                    >
                      <VStack align="start" gap={0}>
                        <Text>{candidate.fullName}</Text>
                        <Text fontSize="sm" color="ink.2">{candidateAddressLine(candidate)}</Text>
                        {candidateDistrictLine(candidate) && (
                          <Text fontSize="xs" color="ink.2">{candidateDistrictLine(candidate)}</Text>
                        )}
                      </VStack>
                    </Button>
                  ))}
                  <Button type="button" variant="ghost" minH="44px" alignSelf="flex-start" onClick={dismissSuggestions}>
                    None of these
                  </Button>
                </VStack>
              </Card>
            )}
          </VStack>
        )}

        {readOnly && (
          <VStack align="stretch" gap={2}>
            <Text fontSize="sm" color="ink.2">Matched to an existing voter-file record. Fields are locked.</Text>
            <Button type="button" variant="ghost" minH="44px" alignSelf="flex-start" onClick={useDifferentPerson}>
              Use a different person
            </Button>
          </VStack>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <Field.Root invalid={!!errors.phone}>
            <Field.Label>Phone</Field.Label>
            <Input type="tel" inputMode="tel" readOnly={readOnly}
              {...register('phone', {
                validate: (v) => !v || v.trim() === '' || isValidPhone(v) || PHONE_ERROR,
              })} />
            {!!errors.phone && <Field.ErrorText>{errors.phone.message}</Field.ErrorText>}
          </Field.Root>
          <Field.Root invalid={!!errors.email}>
            <Field.Label>Email</Field.Label>
            <Input type="email" readOnly={readOnly}
              {...register('email', {
                validate: (v) => !v || v.trim() === '' || isValidEmail(v) || EMAIL_ERROR,
              })} />
            {!!errors.email && <Field.ErrorText>{errors.email.message}</Field.ErrorText>}
          </Field.Root>
        </SimpleGrid>

        {submitError && <Text fontSize="sm" color="danger.fg">{submitError}</Text>}

        {isMobile ? (
          <StickyActionFooter>
            {readOnly ? (
              <Button type="button" colorPalette="brand" onClick={submitAdopted}>
                Add this contact
              </Button>
            ) : (
              <Button type="submit" colorPalette="brand" loading={createContact.isPending}>
                Create contact
              </Button>
            )}
          </StickyActionFooter>
        ) : readOnly ? (
          <Button type="button" colorPalette="brand" onClick={submitAdopted} alignSelf="flex-start">
            Add this contact
          </Button>
        ) : (
          <Button type="submit" colorPalette="brand" loading={createContact.isPending} alignSelf="flex-start">
            Create contact
          </Button>
        )}
      </VStack>
    </form>
  )
}
